import { type ComponentType, Fragment, type HTMLProps, type ReactNode } from 'react';
import type { Row } from '@tanstack/react-table';
import { clsx } from 'clsx';
import type { RowLevelInteractionBuilder } from '../row-context';
import { TR } from './tr/tr';
import { TRGroup } from './tr/tr-group';
import styles from './table-body.module.scss';

// This component is used to actually render the tbody element. A fragment will be rendered if there are grouped rows instead.
function ParentTableBody({ children, className, ...rest }: HTMLProps<HTMLTableSectionElement>) {
  return (
    <tbody className={clsx(styles.tableBody, className)} {...rest}>
      {children}
    </tbody>
  );
}

interface TableBodyProps<TData extends object> {
  defaultCellRenderWhenEmpty?: ReactNode;
  groupRowsBy?: (row: TData) => string;
  rowLevelInteractionBuilder: RowLevelInteractionBuilder<TData> | undefined;
  rows: Row<TData>[];
  showSkeleton: boolean | undefined;
  SubRowComponent?: ComponentType<TData>;
  totalRows: number;
}

// TODO: foot gun, groupRowsBy will only work when rows are sorted in a way where groups will be adjacent
export function TableBody<TData extends object>({
  defaultCellRenderWhenEmpty,
  groupRowsBy,
  rowLevelInteractionBuilder,
  rows,
  showSkeleton,
  SubRowComponent,
  totalRows,
}: TableBodyProps<TData>) {
  // 'use no memo' directive is required for now because react-table v8 is not compatible with React 19's compiler just yet
  'use no memo';

  const hasExpandingRows = rows.some((row) => SubRowComponent && row.getCanExpand());
  const ParentElement = hasExpandingRows ? Fragment : ParentTableBody;

  return (
    <ParentElement>
      {rows.map((row, i, arr) => {
        const baseRow = (
          <TR<TData>
            canExpand={row.getCanExpand()}
            cells={row.getVisibleCells()}
            defaultCellRenderWhenEmpty={defaultCellRenderWhenEmpty}
            isExpanded={row.getIsExpanded()}
            key={row.id}
            row={row}
            rowLevelInteractionBuilder={rowLevelInteractionBuilder}
            SubRowComponent={SubRowComponent}
            showSkeleton={showSkeleton}
            totalRows={totalRows}
          />
        );

        const lastGroup = arr[i - 1]?.original ? groupRowsBy?.(arr[i - 1].original) : undefined;
        const group = groupRowsBy?.(row.original);
        if (group && group !== lastGroup) {
          return (
            <Fragment key={group}>
              <TRGroup>{group}</TRGroup>
              {baseRow}
            </Fragment>
          );
        }

        return baseRow;
      })}
    </ParentElement>
  );
}
